import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Col, Descriptions, Divider, Form, Input, message, Modal, Row, Space, Switch, Table } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import Title from "antd/es/typography/Title";
import moment from "moment";

// Firebase
import firebase from "firebase/compat/app";
import { stamp } from "../../../modules/helpers/audit";

import { shallowEqual, useSelector, useDispatch } from 'react-redux';

// Default send options - all enabled by default
const DEFAULT_SEND_OPTIONS = {
  types: {
    simple: true,
    recommande: true,
    electronique: true,
  },
  printing: {
    color_option: {
      black_white: true,
      color: true,
    },
    face_option: {
      one_face: true,
      double_face: true,
    },
  },
  simple: {
    recipients_type: {
      unique: true,
      multiple: true,
    },
    envelope: {
      "J+3": true,
      "J+4": true,
    },
  },
  recommande: {
    receipt_aknowledge: {
      yes: true,
      no: true,
    },
    level: {
      R1: true,
      R2: true,
      R3: true,
    },
  },
};

function SendOptionsModal({ visible, sendOptions, onSubmit, onCancel, isLoading }) {
  const [options, setOptions] = useState(sendOptions || DEFAULT_SEND_OPTIONS);

  useEffect(() => {
    if (visible) {
      setOptions(sendOptions || DEFAULT_SEND_OPTIONS);
    }
  }, [visible, sendOptions]);

  // Helper to count checked options in a group
  const countChecked = (group) => {
    if (!group) return 0;
    return Object.values(group).filter(Boolean).length;
  };

  const handleTypeChange = (type, checked) => {
    // Prevent unchecking if it's the last one
    if (!checked && countChecked(options.types) <= 1) {
      message.warning('Au moins un type d\'envoi doit être sélectionné');
      return;
    }
    setOptions((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        [type]: checked,
      },
    }));
  };

  const handleOptionChange = (category, field, value, checked) => {
    // Prevent unchecking if it's the last one in the group
    if (!checked && countChecked(options[category]?.[field]) <= 1) {
      message.warning('Au moins une option doit être sélectionnée dans ce groupe');
      return;
    }
    setOptions((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: {
          ...prev[category]?.[field],
          [value]: checked,
        },
      },
    }));
  };

  return (
    <Modal
      visible={visible}
      title="Options d'envoi autorisées"
      okText="Enregistrer"
      cancelText="Annuler"
      width={700}
      centered
      maskClosable={false}
      onCancel={onCancel}
      onOk={() => onSubmit(options)}
      okButtonProps={{ loading: isLoading }}
    >
      <Card title="Types d'envoi" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical">
          <Checkbox
            checked={options.types?.simple}
            onChange={(e) => handleTypeChange('simple', e.target.checked)}
          >
            Lettre simple
          </Checkbox>
          <Checkbox
            checked={options.types?.recommande}
            onChange={(e) => handleTypeChange('recommande', e.target.checked)}
          >
            Lettre recommandée
          </Checkbox>
          <Checkbox
            checked={options.types?.electronique}
            onChange={(e) => handleTypeChange('electronique', e.target.checked)}
          >
            Envoi électronique
          </Checkbox>
        </Space>
      </Card>

      <Card title="Options d'impression" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Couleur</div>
            <Space direction="vertical">
              <Checkbox
                checked={options.printing?.color_option?.black_white}
                onChange={(e) => handleOptionChange('printing', 'color_option', 'black_white', e.target.checked)}
              >
                Noir/Blanc
              </Checkbox>
              <Checkbox
                checked={options.printing?.color_option?.color}
                onChange={(e) => handleOptionChange('printing', 'color_option', 'color', e.target.checked)}
              >
                Couleur
              </Checkbox>
            </Space>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Impression</div>
            <Space direction="vertical">
              <Checkbox
                checked={options.printing?.face_option?.one_face}
                onChange={(e) => handleOptionChange('printing', 'face_option', 'one_face', e.target.checked)}
              >
                Recto seulement
              </Checkbox>
              <Checkbox
                checked={options.printing?.face_option?.double_face}
                onChange={(e) => handleOptionChange('printing', 'face_option', 'double_face', e.target.checked)}
              >
                Recto/verso
              </Checkbox>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card title="Options Lettre simple" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Type de destinataires</div>
            <Space direction="vertical">
              <Checkbox
                checked={options.simple?.recipients_type?.unique}
                onChange={(e) => handleOptionChange('simple', 'recipients_type', 'unique', e.target.checked)}
              >
                Destinataire unique
              </Checkbox>
              <Checkbox
                checked={options.simple?.recipients_type?.multiple}
                onChange={(e) => handleOptionChange('simple', 'recipients_type', 'multiple', e.target.checked)}
              >
                Destinataires multiples
              </Checkbox>
            </Space>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Délai de distribution</div>
            <Space direction="vertical">
              <Checkbox
                checked={options.simple?.envelope?.["J+3"]}
                onChange={(e) => handleOptionChange('simple', 'envelope', 'J+3', e.target.checked)}
              >
                J+3
              </Checkbox>
              <Checkbox
                checked={options.simple?.envelope?.["J+4"]}
                onChange={(e) => handleOptionChange('simple', 'envelope', 'J+4', e.target.checked)}
              >
                J+4
              </Checkbox>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card title="Options Lettre recommandée" size="small">
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Accusé de réception</div>
            <Space direction="vertical">
              <Checkbox
                checked={options.recommande?.receipt_aknowledge?.yes}
                onChange={(e) => handleOptionChange('recommande', 'receipt_aknowledge', 'yes', e.target.checked)}
              >
                Avec accusé de réception
              </Checkbox>
              <Checkbox
                checked={options.recommande?.receipt_aknowledge?.no}
                onChange={(e) => handleOptionChange('recommande', 'receipt_aknowledge', 'no', e.target.checked)}
              >
                Sans accusé de réception
              </Checkbox>
            </Space>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Niveau de recommandation</div>
            <Space direction="vertical">
              <Checkbox
                checked={options.recommande?.level?.R1}
                onChange={(e) => handleOptionChange('recommande', 'level', 'R1', e.target.checked)}
              >
                R1
              </Checkbox>
              <Checkbox
                checked={options.recommande?.level?.R2}
                onChange={(e) => handleOptionChange('recommande', 'level', 'R2', e.target.checked)}
              >
                R2
              </Checkbox>
              <Checkbox
                checked={options.recommande?.level?.R3}
                onChange={(e) => handleOptionChange('recommande', 'level', 'R3', e.target.checked)}
              >
                R3
              </Checkbox>
            </Space>
          </Col>
        </Row>
      </Card>
    </Modal>
  );
}

function CompanyForm({ values, form }) {
  return (
      <Form layout="vertical" form={form} initialValues={values}>
        <Form.Item
          name="name"
          label="Nom"
          rules={[{ required: true, message: 'Veuillez entrer un nom' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email de contact"
          rules={[{ required: true, message: 'Veuillez entrer un email' }, { type: 'email', message: 'Veuillez entrer un email valide' }]}
        >
          <Input placeholder="Ex: entreprise@email.com" />
        </Form.Item>

        <Form.Item
          name="address_line_1"
          label="Adresse ligne 1"
          rules={[{ required: true, message: 'Veuillez entrer une adresse valide' }]}
        >
          <Input placeholder="Adresse postale, boîte postale..." />
        </Form.Item>

        <Form.Item
          name="address_line_2"
          label="Adresse ligne 2"
          rules={[{ required: false }]}
        >
          <Input placeholder="Appartement, suite, unité, immeuble, étage, etc." />
        </Form.Item>

        <Form.Item
          name="postal_code"
          label="Code postal"
          rules={[{ required: true, message: 'Veuillez entrer un code postal' }]}
        >
          <Input placeholder="Ex: 75008" />
        </Form.Item>

        <Form.Item
          name="city"
          label="Ville"
          rules={[{ required: true, message: 'Veuillez entrer une ville' }]}
        >
          <Input placeholder="Ex: Paris" />
        </Form.Item>

        <Form.Item
          name="country"
          label="Pays"
          initialValue="France"
        >
          <Input placeholder="Ex: France" />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Téléphone"
        >
          <Input placeholder="Ex: 0123456789" />
        </Form.Item>
      </Form>
    );
}

function EditModal({ visible, company, onSubmit, onCancel, isFormLoading, setIsFormLoading }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible === true) {
      setIsFormLoading(false);
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      title="Modifier les informations"
      okText="Modifier"
      canceltext="Annuler"
      maskClosable={false}
      onCancel={() => {
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            setIsFormLoading(true);
            onSubmit(values, form.resetFields);
          })
          .catch(() => {
            // Validation failed
          });
      }}
      okButtonProps={{ loading: isFormLoading }}
    >
      <CompanyForm form={form} values={company} />
    </Modal>
  );
}

function SubAccountModal({ visible, onCancel, onSubmit, loading, parentCompany }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && parentCompany) {
      form.resetFields();
      form.setFieldsValue({
        accountName: `${parentCompany.name} - `,
        address_line_1: parentCompany.address_line_1 || "",
        address_line_2: parentCompany.address_line_2 || "",
        postal_code: parentCompany.postal_code || "",
        city: parentCompany.city || "",
        phone: parentCompany.phone || "",
      });
    }
  }, [visible, form, parentCompany]);

  return (
    <Modal
      visible={visible}
      title="Créer un sous-compte"
      okText="Créer et inviter"
      cancelText="Annuler"
      maskClosable={false}
      width={700}
      confirmLoading={loading}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => onSubmit(values, form.resetFields))
          .catch(() => {});
      }}
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Title level={5} style={{ marginBottom: 16 }}>
          Informations du service
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="accountName"
              label="Nom du sous-compte"
              rules={[{ required: true, message: "Requis" }]}
            >
              <Input placeholder="Ex: Mon entreprise - Service RH" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="serviceEmail"
              label="Email de contact"
              rules={[
                { required: true, message: "Requis" },
                { type: "email", message: "Email invalide" },
              ]}
            >
              <Input placeholder="Ex: rh@hopital.org" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="address_line_1"
          label="Adresse ligne 1"
          rules={[{ required: true, message: "Requis" }]}
        >
          <Input placeholder="Adresse postale, boîte postale..." />
        </Form.Item>

        <Form.Item name="address_line_2" label="Adresse ligne 2">
          <Input placeholder="Appartement, suite, unité, immeuble, étage, etc." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="postal_code"
              label="Code postal"
              rules={[{ required: true, message: "Requis" }]}
            >
              <Input placeholder="Ex: 75008" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="city"
              label="Ville"
              rules={[{ required: true, message: "Requis" }]}
            >
              <Input placeholder="Ex: Paris" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="phone" label="Téléphone">
              <Input placeholder="Ex: 0123456789" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="country" label="Pays" initialValue="France">
          <Input placeholder="Ex: France" />
        </Form.Item>

        <Divider />

        <Title level={5} style={{ marginBottom: 8 }}>
          Premier administrateur du service
        </Title>
        <p style={{ color: "#666", marginBottom: 16, fontSize: 13 }}>
          Cette personne recevra une invitation par email et sera administrateur du sous-compte.
        </p>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="adminFirstName"
              label="Prénom"
              rules={[{ required: true, message: "Requis" }]}
            >
              <Input placeholder="Ex: Fabienne" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="adminLastName"
              label="Nom"
              rules={[{ required: true, message: "Requis" }]}
            >
              <Input placeholder="Ex: Martin" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="adminEmail"
          label="Email"
          rules={[
            { required: true, message: "Requis" },
            { type: "email", message: "Email invalide" },
          ]}
        >
          <Input placeholder="Ex: fabienne.martin@hopital.org" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function () {
  const db = firebase.firestore();
  const dispatch = useDispatch();
  const createSubAccount = firebase.app().functions("europe-west1").httpsCallable("createSubAccount");
  const authUser = useSelector((state) => state.user, shallowEqual);
  const [company, setCompany] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [sendOptionsModalVisible, setSendOptionsModalVisible] = useState(false);
  const [isSendOptionsLoading, setIsSendOptionsLoading] = useState(false);
  const [subAccounts, setSubAccounts] = useState([]);
  const [subAccountModalVisible, setSubAccountModalVisible] = useState(false);
  const [subAccountLoading, setSubAccountLoading] = useState(false);

  useEffect(() => {
    db
      .collection('users')
      .doc(authUser.uid)
      .get()
      .then((snapshot) => {
        const userData = snapshot.data();

        if (userData.company) {
          userData.company.get().then((doc) => {
            const companyData = doc.data();
            setCompany({ ...companyData, id: doc.id });
          });
        }
      });
  }, [db, authUser.uid]);

  useEffect(() => {
    if (!company?.allowSubAccounts) return;

    const unsubscribe = db
      .collection("companies")
      .where("parentCompany", "==", db.collection("companies").doc(company.id))
      .onSnapshot(async (snapshot) => {
        const accounts = [];

        for (const doc of snapshot.docs) {
          const usersSnap = await db
            .collection("users")
            .where("company", "==", doc.ref)
            .get();

          accounts.push({
            ...doc.data(),
            id: doc.id,
            key: doc.id,
            userCount: usersSnap.size,
          });
        }

        setSubAccounts(accounts);
      });

    return () => unsubscribe();
  }, [company]);

  const handleCreateSubAccount = async (values, resetFields) => {
    setSubAccountLoading(true);
    try {
      await createSubAccount({
        parentCompanyId: company.id,
        accountName: values.accountName,
        serviceEmail: values.serviceEmail,
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2 || "",
        postal_code: values.postal_code,
        city: values.city,
        country: values.country || "France",
        phone: values.phone || "",
        adminFirstName: values.adminFirstName,
        adminLastName: values.adminLastName,
        adminEmail: values.adminEmail,
      });

      message.success(
        `Sous-compte "${values.accountName}" créé ! Invitation envoyée à ${values.adminEmail}`
      );
      resetFields();
      setSubAccountModalVisible(false);
    } catch (error) {
      console.error("Error creating sub-account:", error);
      const errorCode = error?.details?.errorCode;
      const ERROR_MESSAGES = {
        NAME_ALREADY_EXISTS: "Ce nom de sous-compte est déjà pris, veuillez en choisir un autre.",
        EMAIL_ALREADY_EXISTS: "Cette adresse email est déjà utilisée.",
      };
      message.error(ERROR_MESSAGES[errorCode] || error?.message || "Erreur lors de la création du sous-compte");
    } finally {
      setSubAccountLoading(false);
    }
  };

  const handleSaveSendOptions = (options) => {
    setIsSendOptionsLoading(true);
    db
      .collection('companies')
      .doc(company.id || authUser.company.id)
      .set({ sendOptions: options, last_change: stamp() }, { merge: true })
      .then(() => {
        // Update local state
        setCompany((prev) => ({ ...prev, sendOptions: options }));
        // Update Redux store so other components get the update immediately
        dispatch({ type: 'UPDATE_COMPANY_DATA', payload: { sendOptions: options } });
        setSendOptionsModalVisible(false);
        message.success('Options d\'envoi enregistrées');
      })
      .catch((e) => {
        message.error('Une erreur est survenue. Veuillez réessayer.');
        console.error(e);
      })
      .finally(() => {
        setIsSendOptionsLoading(false);
      });
  };

  return (
    <>
      <h1>Mon entreprise</h1>

      {authUser.is_company_admin && (
        <Row style={{ marginBottom: 16 }}>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button
              type="default"
              icon={<SettingOutlined />}
              onClick={() => setSendOptionsModalVisible(true)}
            >
              Options d'envoi
            </Button>
          </Col>
        </Row>
      )}

      {company && (
        <Descriptions bordered column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="Nom">{company.name}</Descriptions.Item>
          <Descriptions.Item label="Téléphone">{company.phone}</Descriptions.Item>
          <Descriptions.Item label="Email de contact">{company.email}</Descriptions.Item>
          <Descriptions.Item label="Adresse">
            {company.address_line_1}<br />
            {company.address_line_2}
          </Descriptions.Item>
          <Descriptions.Item label="Ville">{company.city}</Descriptions.Item>
          <Descriptions.Item label="Code postal">{company.postal_code}</Descriptions.Item>
          <Descriptions.Item label="Pays">{company.country || "France"}</Descriptions.Item>
        </Descriptions>
      )}

      {/* Edit Modal */}
      <EditModal
        visible={modalVisible}
        company={company}
        onCancel={() => { setModalVisible(false); }}
        isFormLoading={isFormLoading}
        setIsFormLoading={setIsFormLoading}
        onSubmit={(values, resetFields) => {
          db
            .collection('companies')
            .doc(authUser.company.id)
            .set(
              { ...values, last_change: stamp() },
              { merge: true },
            )
            .then(() => {
              resetFields();
              setModalVisible(false);
              message.success('Modifications enregistrées');
              setCompany((prev) => ({ ...prev, ...values }));
            })
            .catch((e) => {
              setIsFormLoading(false);
              message.error('Une erreur est survenue. Veuillez réessayer.');
              console.error(e);
            });
        }}
      />

      {/* Send Options Modal */}
      <SendOptionsModal
        visible={sendOptionsModalVisible}
        sendOptions={company?.sendOptions}
        onCancel={() => setSendOptionsModalVisible(false)}
        isLoading={isSendOptionsLoading}
        onSubmit={handleSaveSendOptions}
      />

      {company?.allowSubAccounts && (
        <>
          <Divider style={{ marginTop: 32 }} />

          <Title level={4}>Sous-comptes / Services</Title>

          {company.maxSubAccounts && (
            <Alert
              message={`${subAccounts.length} / ${company.maxSubAccounts} sous-comptes utilisés`}
              type={subAccounts.length >= company.maxSubAccounts ? "warning" : "info"}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {authUser.is_company_admin && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setSubAccountModalVisible(true)}
              disabled={company.maxSubAccounts && subAccounts.length >= company.maxSubAccounts}
              style={{ marginBottom: 16 }}
            >
              Créer un sous-compte
            </Button>
          )}

          <Table
            dataSource={subAccounts}
            rowKey="id"
            size="small"
            bordered
            locale={{ emptyText: "Aucun sous-compte créé" }}
            pagination={false}
            columns={[
              {
                title: "Nom",
                dataIndex: "name",
                render: (name) => name || "-",
              },
              { title: "Email", dataIndex: "email" },
              {
                title: "Utilisateurs",
                dataIndex: "userCount",
                align: "center",
                width: 100,
              },
              {
                title: "Créé le",
                dataIndex: "createdAt",
                width: 120,
                render: (d) => (d ? moment(d.toDate()).format("DD/MM/YYYY") : "-"),
              },
            ]}
          />

          <SubAccountModal
            visible={subAccountModalVisible}
            onCancel={() => setSubAccountModalVisible(false)}
            onSubmit={handleCreateSubAccount}
            loading={subAccountLoading}
            parentCompany={company}
          />
        </>
      )}
    </>
  );
}
